import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Layout from "../../components/Layout";
import apiClient from "../../services/apiClient";
import { ArrowLeft, Calendar } from "lucide-react";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TextField } from '@mui/material';
import { Switch, FormControlLabel, Button as MuiButton, IconButton } from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import dayjs, { Dayjs } from 'dayjs';
interface Slot {
    id: number;
    day: string;
    visit_type: string;
    timing: string;
    provider_id: number;
    time_range?: { start: string; end: string } | string;
    service_id?: number;
    custom_unavailabilities?: { start: string; end: string }[];
}
interface FormData {
    id?: number;
    day: string;
    visit_type: string;
    service_id?: number;
    start: string;
    end: string;
    custom_unavailabilities?: { start: string; end: string }[];
}
interface Filter {
    value: Dayjs | null;
    onChange: (value: Dayjs | null) => void;
}
const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const visitTypes = ['Tele Consult', 'Walk In', 'Home Visit'];
interface AvailabilityModalProps {
    isOpen: boolean;
    mode: 'add' | 'edit';
    formData: FormData;
    onClose: () => void;
    onSubmit: (e: React.FormEvent) => void;
    onFieldChange: (field: keyof FormData, value: any) => void;
    modalError: string;
    daysOfWeek: string[];
    visitTypes: string[];
    fromType: string;
    services: any[];
}
type UnavailSlot = {
    start: string;
    end: string;
    startTime: Dayjs | null;
    endTime: Dayjs | null;
};
const AvailabilityModal: React.FC<AvailabilityModalProps> = ({
    isOpen,
    mode,
    formData,
    onClose,
    onSubmit,
    onFieldChange,
    modalError,
    daysOfWeek,
    visitTypes,
    fromType,
    services,
}) => {
    const [startTime, setStartTime] = useState<Dayjs | null>(null);
    const [endTime, setEndTime] = useState<Dayjs | null>(null);
    const [unavailSlots, setUnavailSlots] = useState<UnavailSlot[]>([]);
    const [hasCustomUnavail, setHasCustomUnavail] = useState(false);
    // Initialize time pickers when form data changes
    useEffect(() => {
        if (formData.start) {
            if (fromType === 'service') {
                setStartTime(dayjs(formData.start, 'hh:mm A'));
            } else {
                const [hours, minutes] = formData.start.split(':');
                setStartTime(dayjs().hour(parseInt(hours)).minute(parseInt(minutes)));
            }
        } else {
            setStartTime(null);
        }
        if (formData.end) {
            if (fromType === 'service') {
                setEndTime(dayjs(formData.end, 'hh:mm A'));
            } else {
                const [hours, minutes] = formData.end.split(':');
                setEndTime(dayjs().hour(parseInt(hours)).minute(parseInt(minutes)));
            }
        } else {
            setEndTime(null);
        }
    }, [formData.start, formData.end, isOpen, fromType]);
    useEffect(() => {
        const slots: UnavailSlot[] = (formData.custom_unavailabilities || []).map((s) => ({
            start: s.start,
            end: s.end,
            startTime: s.start ? dayjs(s.start, 'hh:mm A') : null,
            endTime: s.end ? dayjs(s.end, 'hh:mm A') : null
        }));
        setUnavailSlots(slots);
    }, [formData.custom_unavailabilities]);
    useEffect(() => {
        setHasCustomUnavail(unavailSlots.length > 0);
    }, [unavailSlots]);
    const handleStartTimeChange = (newValue: Dayjs | null) => {
        setStartTime(newValue);
        if (newValue) {
            const timeString = fromType === 'service' ? newValue.format('hh:mm A') : newValue.format('HH:mm');
            onFieldChange('start', timeString);
        } else {
            onFieldChange('start', '');
        }
    };
    const handleEndTimeChange = (newValue: Dayjs | null) => {
        setEndTime(newValue);
        if (newValue) {
            const timeString = fromType === 'service' ? newValue.format('hh:mm A') : newValue.format('HH:mm');
            onFieldChange('end', timeString);
        } else {
            onFieldChange('end', '');
        }
    };
    const handleToggleCustom = (checked: boolean) => {
        if (checked) {
            setHasCustomUnavail(true);
            if (unavailSlots.length === 0) {
                addUnavailSlot();
            }
        } else {
            setUnavailSlots([]);
            onFieldChange('custom_unavailabilities', []);
        }
    };
    const addUnavailSlot = () => {
        const newUnavail: UnavailSlot = { start: '', end: '', startTime: null, endTime: null };
        const newSlots = [...unavailSlots, newUnavail];
        setUnavailSlots(newSlots);
        const newStrSlots = newSlots.map((s) => ({ start: s.start, end: s.end }));
        onFieldChange('custom_unavailabilities', newStrSlots);
    };
    const removeUnavailSlot = (index: number) => {
        const newSlots = unavailSlots.filter((_, i) => i !== index);
        setUnavailSlots(newSlots);
        const newStrSlots = newSlots.map((s) => ({ start: s.start, end: s.end }));
        onFieldChange('custom_unavailabilities', newStrSlots);
    };
    const updateSlot = (index: number, type: 'start' | 'end', value: Dayjs | null) => {
        setUnavailSlots((prev) => {
            const newSlots = [...prev];
            const sl = newSlots[index];
            if (type === 'start') {
                sl.startTime = value;
                sl.start = value ? value.format('hh:mm A') : '';
            } else {
                sl.endTime = value;
                sl.end = value ? value.format('hh:mm A') : '';
            }
            const newStrSlots = newSlots.map((s) => ({ start: s.start, end: s.end }));
            onFieldChange('custom_unavailabilities', newStrSlots);
            return newSlots;
        });
    };
    if (!isOpen) return null;
    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <div
                style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: "rgba(0, 0, 0, 0.5)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 1000,
                }}
            >
                <div
                    style={{
                        backgroundColor: "white",
                        padding: fromType === 'service' ? "20px" : "32px",
                        borderRadius: "16px",
                        maxWidth: fromType === 'service' ? "600px" : "400px",
                        width: "90%",
                        maxHeight: fromType === 'service' ? "80vh" : "auto",
                        overflowY: "auto",
                        boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)",
                    }}
                >
                    <h2
                        style={{
                            margin: "0 0 24px 0",
                            fontSize: "1.5rem",
                            fontWeight: 600,
                            color: "#1f2937",
                        }}
                    >
                        {mode === "add" ? "Add Availability Slot" : "Edit Availability Slot"}
                    </h2>
                    {modalError && (
                        <div
                            style={{
                                color: "#ef4444",
                                backgroundColor: "#fef2f2",
                                padding: "8px 12px",
                                borderRadius: "6px",
                                marginBottom: "16px",
                                fontSize: "0.875rem",
                                whiteSpace: "pre-line",
                            }}
                            dangerouslySetInnerHTML={{ __html: modalError.replace(/\n/g, '<br />') }}
                        />
                    )}
                    <form onSubmit={onSubmit}>
                        <div style={{ marginBottom: "16px" }}>
                            <label
                                style={{
                                    display: "block",
                                    marginBottom: "8px",
                                    fontWeight: 500,
                                    color: "#374151",
                                }}
                            >
                                Day *
                            </label>
                            <select
                                value={formData.day}
                                onChange={(e) => onFieldChange('day', e.target.value)}
                                required
                                style={{
                                    width: "100%",
                                    padding: "10px 12px",
                                    border: "1px solid #d1d5db",
                                    borderRadius: "8px",
                                    fontSize: "1rem",
                                }}
                            >
                                {mode === 'add' && <option value="All">All Days</option>}
                                {daysOfWeek.map((day) => (
                                    <option key={day} value={day}>
                                        {day}
                                    </option>
                                ))}
                            </select>
                        </div>
                        {fromType !== 'service' ? (
                            <div style={{ marginBottom: "16px" }}>
                                <label
                                    style={{
                                        display: "block",
                                        marginBottom: "8px",
                                        fontWeight: 500,
                                        color: "#374151",
                                    }}
                                >
                                    Visit Type *
                                </label>
                                <select
                                    value={formData.visit_type}
                                    onChange={(e) => onFieldChange('visit_type', e.target.value)}
                                    required
                                    style={{
                                        width: "100%",
                                        padding: "10px 12px",
                                        border: "1px solid #d1d5db",
                                        borderRadius: "8px",
                                        fontSize: "1rem",
                                    }}
                                >
                                    <option value="">Select visit type</option>
                                    {visitTypes.map((type) => (
                                        <option key={type} value={type}>
                                            {type}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        ) : (
                            <div style={{ marginBottom: "16px" }}>
                                <label
                                    style={{
                                        display: "block",
                                        marginBottom: "8px",
                                        fontWeight: 500,
                                        color: "#374151",
                                    }}
                                >
                                    Service *
                                </label>
                                {mode === 'add' ? (
                                    <select
                                        value={formData.service_id?.toString() || ""}
                                        onChange={(e) => {
                                            const idStr = e.target.value;
                                            if (idStr === '') {
                                                onFieldChange('service_id', undefined);
                                                onFieldChange('visit_type', '');
                                            } else {
                                                const id = parseInt(idStr);
                                                const service = services.find(s => s.id === id);
                                                if (service) {
                                                    onFieldChange('visit_type', service.services_name);
                                                    onFieldChange('service_id', id);
                                                }
                                            }
                                        }}
                                        required
                                        style={{
                                            width: "100%",
                                            padding: "10px 12px",
                                            border: "1px solid #d1d5db",
                                            borderRadius: "8px",
                                            fontSize: "1rem",
                                        }}
                                    >
                                        <option value="">Select service</option>
                                        {services.map((s) => (
                                            <option key={s.id} value={s.id}>
                                                {s.services_name}
                                            </option>
                                        ))}
                                    </select>
                                ) : (
                                    <div
                                        style={{
                                            width: "100%",
                                            padding: "10px 12px",
                                            backgroundColor: "#f9fafb",
                                            border: "1px solid #d1d5db",
                                            borderRadius: "8px",
                                            fontSize: "1rem",
                                            color: "#374151",
                                        }}
                                    >
                                        {formData.visit_type || 'Unknown Service'}
                                    </div>
                                )}
                            </div>
                        )}
                        <div style={{ marginBottom: "16px" }}>
                            <label
                                style={{
                                    display: "block",
                                    marginBottom: "8px",
                                    fontWeight: 500,
                                    color: "#374151",
                                }}
                            >
                                Start Time *
                            </label>
                            <TimePicker
                                ampm={fromType === 'service'}
                                value={startTime}
                                onChange={(newValue) => {
                                    handleStartTimeChange(newValue ? dayjs(newValue) : null);
                                }}
                                slotProps={{
                                    textField: {
                                        required: true,
                                        fullWidth: true,
                                        size: "small",
                                        sx: {
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '8px',
                                            }
                                        }
                                    }
                                }}
                            />
                        </div>
                        <div style={{ marginBottom: "16px" }}>
                            <label
                                style={{
                                    display: "block",
                                    marginBottom: "8px",
                                    fontWeight: 500,
                                    color: "#374151",
                                }}
                            >
                                End Time *
                            </label>
                            <TimePicker
                                ampm={fromType === 'service'}
                                value={endTime}
                                onChange={(newValue) => {
                                    handleEndTimeChange(newValue ? dayjs(newValue) : null);
                                }}
                                slotProps={{
                                    textField: {
                                        required: true,
                                        fullWidth: true,
                                        size: "small",
                                        sx: {
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '8px',
                                            }
                                        }
                                    }
                                }}
                            />
                        </div>
                        {fromType === 'service' && (
                            <div style={{ marginBottom: "16px" }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={hasCustomUnavail}
                                            onChange={(e) => handleToggleCustom(e.target.checked)}
                                        />
                                    }
                                    label="Enable Custom Unavailability Slots"
                                />
                            </div>
                        )}
                        {hasCustomUnavail && fromType === 'service' && (
                            <div style={{ marginBottom: "24px" }}>
                                <label
                                    style={{
                                        display: "block",
                                        marginBottom: "8px",
                                        fontWeight: 500,
                                        color: "#374151",
                                    }}
                                >
                                    Custom Unavailability Slots
                                </label>
                                {unavailSlots.map((slot, index) => (
                                    <div
                                        key={index}
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "10px",
                                            marginBottom: "10px"
                                        }}
                                    >
                                        <TimePicker
                                            ampm={true}
                                            value={slot.startTime}
                                            onChange={(v) => updateSlot(index, 'start', v ? dayjs(v) : null)}
                                            slotProps={{
                                                textField: {
                                                    size: "small",
                                                    fullWidth: false,
                                                    sx: { width: "120px" }
                                                }
                                            }}
                                        />
                                        <span>-</span>
                                        <TimePicker
                                            ampm={true}
                                            value={slot.endTime}
                                            onChange={(v) => updateSlot(index, 'end', v ? dayjs(v) : null)}
                                            slotProps={{
                                                textField: {
                                                    size: "small",
                                                    fullWidth: false,
                                                    sx: { width: "120px" }
                                                }
                                            }}
                                        />
                                        <IconButton
                                            onClick={() => removeUnavailSlot(index)}
                                            size="small"
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </div>
                                ))}
                                <MuiButton
                                    variant="outlined"
                                    startIcon={<AddIcon />}
                                    onClick={addUnavailSlot}
                                    size="small"
                                >
                                    Add Unavailability Slot
                                </MuiButton>
                            </div>
                        )}
                        <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}>
                            <button
                                type="button"
                                onClick={onClose}
                                style={{
                                    padding: "10px 20px",
                                    backgroundColor: "#f3f4f6",
                                    color: "#374151",
                                    border: "none",
                                    borderRadius: "8px",
                                    cursor: "pointer",
                                    fontSize: "1rem",
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                style={{
                                    padding: "10px 20px",
                                    backgroundColor: "#10b981",
                                    color: "white",
                                    border: "none",
                                    borderRadius: "8px",
                                    cursor: "pointer",
                                    fontSize: "1rem",
                                    fontWeight: 500,
                                }}
                            >
                                Save Changes
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </LocalizationProvider>
    );
};
interface UnavailFormData {
    id?: number;
    visit_type: string;
    from_date: Dayjs | null;
    to_date: Dayjs | null;
    from_time: Dayjs | null;
    to_time: Dayjs | null;
}
interface UnavailabilityModalProps {
    isOpen: boolean;
    mode: 'add' | 'edit';
    formData: UnavailFormData;
    onClose: () => void;
    onSubmit: (e: React.FormEvent) => void;
    onFieldChange: (field: keyof UnavailFormData, value: Dayjs | null | string) => void;
    modalError: string;
    visitTypes: string[];
}
const UnavailabilityModal: React.FC<UnavailabilityModalProps> = ({
    isOpen,
    mode,
    formData,
    onClose,
    onSubmit,
    onFieldChange,
    modalError,
    visitTypes,
}) => {
    const [fromDate, setFromDate] = useState<Dayjs | null>(null);
    const [toDate, setToDate] = useState<Dayjs | null>(null);
    const [fromTime, setFromTime] = useState<Dayjs | null>(null);
    const [toTime, setToTime] = useState<Dayjs | null>(null);
    useEffect(() => {
        setFromDate(formData.from_date || null);
        setToDate(formData.to_date || null);
        setFromTime(formData.from_time || null);
        setToTime(formData.to_time || null);
    }, [formData, isOpen]);
    const handleFromDateChange = (newValue: Dayjs | null) => {
        setFromDate(newValue);
        onFieldChange('from_date', newValue);
    };
    const handleToDateChange = (newValue: Dayjs | null) => {
        setToDate(newValue);
        onFieldChange('to_date', newValue);
    };
    const handleFromTimeChange = (newValue: Dayjs | null) => {
        setFromTime(newValue);
        onFieldChange('from_time', newValue);
    };
    const handleToTimeChange = (newValue: Dayjs | null) => {
        setToTime(newValue);
        onFieldChange('to_time', newValue);
    };
    if (!isOpen) return null;
    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <div
                style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: "rgba(0, 0, 0, 0.5)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 1000,
                }}
            >
                <div
                    style={{
                        backgroundColor: "white",
                        padding: "32px",
                        borderRadius: "16px",
                        maxWidth: "500px",
                        width: "90%",
                        maxHeight: "80vh",
                        overflowY: "auto",
                        boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)",
                    }}
                >
                    <h2
                        style={{
                            margin: "0 0 24px 0",
                            fontSize: "1.5rem",
                            fontWeight: 600,
                            color: "#1f2937",
                        }}
                    >
                        {mode === "add" ? "Add Unavailability" : "Edit Unavailability"}
                    </h2>
                    {modalError && (
                        <div
                            style={{
                                color: "#ef4444",
                                backgroundColor: "#fef2f2",
                                padding: "8px 12px",
                                borderRadius: "6px",
                                marginBottom: "16px",
                                fontSize: "0.875rem",
                            }}
                        >
                            {modalError}
                        </div>
                    )}
                    <form onSubmit={onSubmit}>
                        <div style={{ marginBottom: "16px" }}>
                            <label
                                style={{
                                    display: "block",
                                    marginBottom: "8px",
                                    fontWeight: 500,
                                    color: "#374151",
                                }}
                            >
                                Visit Type *
                            </label>
                            <select
                                value={formData.visit_type}
                                onChange={(e) => onFieldChange('visit_type', e.target.value)}
                                required
                                style={{
                                    width: "100%",
                                    padding: "10px 12px",
                                    border: "1px solid #d1d5db",
                                    borderRadius: "8px",
                                    fontSize: "1rem",
                                }}
                            >
                                <option value="">Select visit type</option>
                                {visitTypes.map((type) => (
                                    <option key={type} value={type}>
                                        {type}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div style={{ marginBottom: "16px" }}>
                            <label
                                style={{
                                    display: "block",
                                    marginBottom: "8px",
                                    fontWeight: 500,
                                    color: "#374151",
                                }}
                            >
                                From Date *
                            </label>
                            <DatePicker
                                value={fromDate}
                                onChange={(newValue) => {
                                    handleFromDateChange(newValue ? dayjs(newValue) : null);
                                }}
                                slotProps={{
                                    textField: {
                                        required: true,
                                        fullWidth: true,
                                        size: "small",
                                        sx: {
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '8px',
                                            }
                                        }
                                    }
                                }}
                            />
                        </div>
                        <div style={{ marginBottom: "16px" }}>
                            <label
                                style={{
                                    display: "block",
                                    marginBottom: "8px",
                                    fontWeight: 500,
                                    color: "#374151",
                                }}
                            >
                                To Date *
                            </label>
                            <DatePicker
                                value={toDate}
                                onChange={(newValue) => {
                                    handleToDateChange(newValue ? dayjs(newValue) : null);
                                }}
                                slotProps={{
                                    textField: {
                                        required: true,
                                        fullWidth: true,
                                        size: "small",
                                        sx: {
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '8px',
                                            }
                                        }
                                    }
                                }}
                            />
                        </div>
                        <div style={{ marginBottom: "16px" }}>
                            <label
                                style={{
                                    display: "block",
                                    marginBottom: "8px",
                                    fontWeight: 500,
                                    color: "#374151",
                                }}
                            >
                                From Time *
                            </label>
                            <TimePicker
                                ampm={true}
                                value={fromTime}
                                onChange={(newValue) => {
                                    handleFromTimeChange(newValue ? dayjs(newValue) : null);
                                }}
                                slotProps={{
                                    textField: {
                                        required: true,
                                        fullWidth: true,
                                        size: "small",
                                        sx: {
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '8px',
                                            }
                                        }
                                    }
                                }}
                            />
                        </div>
                        <div style={{ marginBottom: "16px" }}>
                            <label
                                style={{
                                    display: "block",
                                    marginBottom: "8px",
                                    fontWeight: 500,
                                    color: "#374151",
                                }}
                            >
                                To Time *
                            </label>
                            <TimePicker
                                ampm={true}
                                value={toTime}
                                onChange={(newValue) => {
                                    handleToTimeChange(newValue ? dayjs(newValue) : null);
                                }}
                                slotProps={{
                                    textField: {
                                        required: true,
                                        fullWidth: true,
                                        size: "small",
                                        sx: {
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '8px',
                                            }
                                        }
                                    }
                                }}
                            />
                        </div>
                        <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}>
                            <button
                                type="button"
                                onClick={onClose}
                                style={{
                                    padding: "10px 20px",
                                    backgroundColor: "#f3f4f6",
                                    color: "#374151",
                                    border: "none",
                                    borderRadius: "8px",
                                    cursor: "pointer",
                                    fontSize: "1rem",
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                style={{
                                    padding: "10px 20px",
                                    backgroundColor: "#ef4444",
                                    color: "white",
                                    border: "none",
                                    borderRadius: "8px",
                                    cursor: "pointer",
                                    fontSize: "1rem",
                                    fontWeight: 500,
                                }}
                            >
                                Save Changes
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </LocalizationProvider>
    );
};
interface SlotCardProps {
    slot: Slot;
    fromType: string;
    services: any[];
    hoveredSlot: number | null;
    onMouseEnter: (id: number) => void;
    onMouseLeave: () => void;
    onEdit: (slot: Slot) => void;
    onDelete: (id: number) => void;
}
const SlotCard: React.FC<SlotCardProps> = ({
    slot,
    fromType,
    services,
    hoveredSlot,
    onMouseEnter,
    onMouseLeave,
    onEdit,
    onDelete,
}) => {
    const rawTiming =
        fromType === "vet"
            ? JSON.parse(slot.timing || "{}")
            : fromType === "service"
                ? slot.time_range || {}
                : {};
    const normalizedTiming = normalizeTiming(rawTiming);
    const start = normalizedTiming.start;
    const end = normalizedTiming.end;
    const customUnavailability = slot.custom_unavailabilities || [];
    const isHovered = hoveredSlot === slot.id;
    const displayVisitType = fromType === 'service' && !slot.visit_type
        ? services.find(s => s.id === slot.service_id)?.services_name || slot.visit_type || 'Unknown Service'
        : slot.visit_type;
    return (
        <div
            onMouseEnter={() => onMouseEnter(slot.id)}
            onMouseLeave={onMouseLeave}
            style={{
                background: "white",
                borderRadius: "16px",
                padding: "24px",
                boxShadow: isHovered
                    ? "0 12px 40px rgba(0, 0, 0, 0.15)"
                    : "0 4px 16px rgba(0, 0, 0, 0.08)",
                transform: isHovered ? "translateY(-4px)" : "translateY(0)",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                border: "2px solid transparent",
                borderColor: isHovered ? "#3b82f6" : "transparent",
            }}
        >
            <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px" }}>
                <div>
                    <h4 style={{ margin: "0 0 4px 0", color: "#1f2937", fontSize: "1.125rem", fontWeight: 600 }}>
                        {displayVisitType}
                    </h4>
                    <p style={{ margin: 0, color: "#6b7280", fontSize: "0.875rem" }}>{slot.day}</p>
                </div>
            </div>
            {(start || end) && (
                <div
                    style={{
                        display: "inline-block",
                        padding: "12px 20px",
                        background: `linear-gradient(135deg,rgb(206, 211, 235),rgb(92, 103, 117))`,
                        color: 'white',
                        borderRadius: "12px",
                        fontWeight: 600,
                        fontSize: "1rem",
                        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                        width: "100%",
                        textAlign: "center",
                        border: "none",
                    }}
                >
                    🕐 {start} - {end}
                </div>
            )}
            {customUnavailability.length > 0 && (
                <div style={{ marginTop: "8px" }}>
                    {customUnavailability.map((cu, index) => (
                        <div
                            key={index}
                            style={{
                                padding: "6px 12px",
                                background: "#fef2f2",
                                color: "#b91c1c",
                                borderRadius: "10px",
                                fontSize: "0.875rem",
                                textAlign: "center",
                                marginTop: "4px",
                            }}
                        >
                            ❌ Unavailable: {cu.start} - {cu.end}
                        </div>
                    ))}
                </div>
            )}
            <div
                style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: "8px",
                    marginTop: "16px",
                }}
            >
                <button
                    onClick={() => onEdit(slot)}
                    style={{
                        padding: "8px",
                        backgroundColor: "#3b82f6",
                        color: "white",
                        border: "none",
                        borderRadius: "50%",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "36px",
                        height: "36px",
                    }}
                    aria-label="Edit"
                >
                    <EditIcon style={{ fontSize: "16px" }} />
                </button>
                <button
                    onClick={() => onDelete(slot.id)}
                    style={{
                        padding: "8px",
                        backgroundColor: "#ef4444",
                        color: "white",
                        border: "none",
                        borderRadius: "50%",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "36px",
                        height: "36px",
                    }}
                    aria-label="Delete"
                >
                    <DeleteIcon style={{ fontSize: "16px" }} />
                </button>
            </div>
        </div>
    );
};
interface UnavailCardProps {
    unavail: any;
    onEdit: (unavail: any) => void;
    onDelete: (id: number) => void;
}
const UnavailCard: React.FC<UnavailCardProps> = ({ unavail, onEdit, onDelete }) => {
    const fromDateStr = dayjs(unavail.from_date).format('MMM DD, YYYY');
    const toDateStr = dayjs(unavail.to_date).format('MMM DD, YYYY');
    const fromTime12 = convertTo12Hour(unavail.timing.from_time);
    const toTime12 = convertTo12Hour(unavail.timing.to_time);
    return (
        <div
            style={{
                background: "white",
                borderRadius: "16px",
                padding: "24px",
                boxShadow: "0 4px 16px rgba(0, 0, 0, 0.08)",
                border: "2px solid #fef2f2",
            }}
        >
            <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px" }}>
                <div>
                    <h4 style={{ margin: "0 0 4px 0", color: "#b91c1c", fontSize: "1.125rem", fontWeight: 600 }}>
                        {unavail.visit_type}
                    </h4>
                    <p style={{ margin: 0, color: "#6b7280", fontSize: "0.875rem" }}>
                        {fromDateStr} - {toDateStr}
                    </p>
                </div>
            </div>
            <div
                style={{
                    display: "inline-block",
                    padding: "12px 20px",
                    background: `linear-gradient(135deg, #f87171, #ef4444)`,
                    color: "white",
                    borderRadius: "12px",
                    fontWeight: 600,
                    fontSize: "1rem",
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                    width: "100%",
                    textAlign: "center",
                    border: "none",
                }}
            >
                {fromTime12} - {toTime12}
            </div>
            <div
                style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: "8px",
                    marginTop: "16px",
                }}
            >
                <button
                    onClick={() => onEdit(unavail)}
                    style={{
                        padding: "8px 16px",
                        backgroundColor: "#3b82f6",
                        color: "white",
                        border: "none",
                        borderRadius: "6px",
                        cursor: "pointer",
                        fontSize: "0.875rem",
                        fontWeight: 500,
                    }}
                >
                    <EditIcon style={{ fontSize: "16px", marginRight: "4px" }} />
                    Edit
                </button>
                <button
                    onClick={() => onDelete(unavail.id)}
                    style={{
                        padding: "8px 16px",
                        backgroundColor: "#ef4444",
                        color: "white",
                        border: "none",
                        borderRadius: "6px",
                        cursor: "pointer",
                        fontSize: "0.875rem",
                        fontWeight: 500,
                    }}
                >
                    <DeleteIcon style={{ fontSize: "16px", marginRight: "4px" }} />
                    Delete
                </button>
            </div>
        </div>
    );
};
interface UnavailabilitySectionProps {
    customSlots: any[];
    onEdit: (unavail: any) => void;
    onDelete: (id: number) => void;
}
const UnavailabilitySection: React.FC<UnavailabilitySectionProps> = ({ customSlots, onEdit, onDelete }) => (
    <div style={{ marginTop: "32px" }}>
        <h2 style={{ fontSize: "1.5rem", fontWeight: 600, marginBottom: "16px" }}>
            Vet Custom Unavailability
        </h2>
        {customSlots.length === 0 ? (
            <p style={{ color: "#6b7280", textAlign: "center", fontSize: "1rem" }}>No custom unavailability found</p>
        ) : (
            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
                    gap: "20px",
                }}
            >
                {customSlots.map((unavail) => (
                    <UnavailCard
                        key={unavail.id}
                        unavail={unavail}
                        onEdit={onEdit}
                        onDelete={onDelete}
                    />
                ))}
            </div>
        )}
    </div>
);
const useAvailability = (providerId: string | undefined, fromType: string) => {
    const [slots, setSlots] = useState<Slot[]>([]);
    const [services, setServices] = useState<any[]>([]);
    const [selectedServiceId, setSelectedServiceId] = useState<number | undefined>(undefined);
    const [customSlots, setCustomSlots] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const fetchCustomUnavailability = async () => {
        if (!providerId) return;
        try {
            const res = await apiClient<any>(`/v1/resource/customAvailabilityDetails?provider_id=${providerId}`);
            setCustomSlots(res?.data?.custom_availability_details || []);
        } catch (err) {
            console.error("Failed to fetch custom availability", err);
        }
    };
    const fetchSlots = async (serviceId?: number) => {
        if (!providerId) return;
        try {
            setLoading(true);
            setError("");
            let url = "";
            if (fromType === "vet") {
                url = `v1/resource/availabilityDetails?provider_id=${providerId}`;
                const res = await apiClient<any>(url);
                const sortedSlots = (res?.data?.availability_details || []).sort(
                    (a: Slot, b: Slot) => daysOfWeek.indexOf(a.day) - daysOfWeek.indexOf(b.day)
                );
                setSlots(sortedSlots);
            } else if (fromType === "service") {
                if (!serviceId) return;
                url = `v1/resource/serviceAvailability?service_provider_id=${providerId}&service_id=${serviceId}`;
                const res = await apiClient<any>(url);
                const sortedSlots = (res?.data?.service_availabilities || []).sort(
                    (a: Slot, b: Slot) => daysOfWeek.indexOf(a.day) - daysOfWeek.indexOf(b.day)
                );
                setSlots(sortedSlots);
            } else {
                setSlots([]);
                throw new Error("Invalid form type");
            }
        } catch (err: any) {
            console.error(err);
            setError("Unable to load availability. Please try again.");
        } finally {
            setLoading(false);
        }
    };
    const fetchServices = async () => {
        if (!providerId) return;
        try {
            setLoading(true);
            setError("");
            const res = await apiClient<any>(`/v1/resource/services?service_provider_id=${providerId}`);
            const fetchedServices = res?.data?.services || [];
            setServices(fetchedServices);
            if (fetchedServices.length > 0) {
                setSelectedServiceId(fetchedServices[0].id);
                fetchSlots(fetchedServices[0].id);
            }
        } catch (err: any) {
            console.error(err);
            setError("Unable to load services. Please try again.");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        if (fromType === "vet") {
            fetchSlots();
            fetchCustomUnavailability();
        } else if (fromType === "service") {
            fetchServices();
        }
    }, [providerId, fromType]);
    return {
        slots,
        services,
        selectedServiceId,
        setSelectedServiceId,
        customSlots,
        loading,
        error,
        setError,
        fetchSlots,
        fetchCustomUnavailability,
    };
};
const getDatesInRange = (from: string, to: string) => {
    const dates: string[] = [];
    let current = new Date(from);
    const end = new Date(to);
    while (current <= end) {
        dates.push(current.toISOString().split("T")[0]);
        current.setDate(current.getDate() + 1);
    }
    return dates;
};
const convertTo12Hour = (time24: string): string => {
    if (!time24) return '';
    const [hoursStr, minutes] = time24.split(':');
    let hours = parseInt(hoursStr);
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;
    const hoursPadded = hours.toString().padStart(2, '0');
    return `${hoursPadded}:${minutes} ${ampm}`;
};
const convertTo24Hour = (time12: string): string => {
    if (!time12) return '';
    const [time, ampm] = time12.split(' ');
    let [hoursStr, minutes] = time.split(':');
    let hours = parseInt(hoursStr);
    if (ampm === 'PM' && hours !== 12) hours += 12;
    if (ampm === 'AM' && hours === 12) hours = 0;
    const hoursPadded = hours.toString().padStart(2, '0');
    return `${hoursPadded}:${minutes}`;
};
const normalizeTiming = (timing: any): { start: string; end: string } => {
    if (typeof timing === 'string') {
        const parts = timing.split('-');
        if (parts.length >= 2) {
            return {
                start: parts[0].trim(),
                end: parts.slice(1).join('-').trim(),
            };
        }
        return { start: '', end: '' };
    } else if (typeof timing === 'object' && timing !== null) {
        return {
            start: timing.start || '',
            end: timing.end || '',
        };
    }
    return { start: '', end: '' };
};
const AvailabilityScreen: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const state = location.state as { providerId: string; fromType: string } | undefined;
    const providerId = state?.providerId;
    const fromType = state?.fromType || "vet";
    const {
        slots,
        services,
        selectedServiceId,
        setSelectedServiceId,
        customSlots,
        loading,
        error,
        setError,
        fetchSlots,
        fetchCustomUnavailability,
    } = useAvailability(providerId, fromType);
    const [hoveredSlot, setHoveredSlot] = useState<number | null>(null);
    const [showModal, setShowModal] = useState(false);
    const [showUnavailModal, setShowUnavailModal] = useState(false);
    const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');
    const [unavailMode, setUnavailMode] = useState<'add' | 'edit'>('add');
    const [formData, setFormData] = useState<FormData>({ day: '', visit_type: '', start: '', end: '', custom_unavailabilities: [] });
    const [unavailFormData, setUnavailFormData] = useState<UnavailFormData>({ visit_type: '', from_date: null, to_date: null, from_time: null, to_time: null });
    const [modalError, setModalError] = useState('');
    const [unavailModalError, setUnavailModalError] = useState('');
    const customSlotsSorted = useMemo(() =>
        [...customSlots].sort((a, b) => dayjs(a.from_date).valueOf() - dayjs(b.from_date).valueOf()),
        [customSlots]
    );
    const getTiming = (slot: Slot) => {
        let raw;
        if (fromType === 'service') {
            raw = slot.time_range || {};
        } else {
            raw = JSON.parse(slot.timing || '{}');
        }
        return normalizeTiming(raw);
    };
    const openAddModal = () => {
        if (fromType === 'service') {
            setFormData({ day: 'All', visit_type: '', service_id: undefined, start: '', end: '', custom_unavailabilities: [] });
        } else {
            setFormData({ day: 'All', visit_type: 'Tele Consult', start: '', end: '', custom_unavailabilities: [] });
        }
        setModalMode('add');
        setModalError('');
        setShowModal(true);
    };
    const openEditModal = (slot: Slot) => {
        const timingObj = getTiming(slot);
        let visitType = slot.visit_type;
        if (fromType === 'service' && !visitType) {
            const serviceId = slot.service_id || selectedServiceId;
            visitType = services.find(s => s.id === serviceId)?.services_name || 'Unknown Service';
        }
        setFormData({
            id: slot.id,
            day: slot.day,
            visit_type: visitType,
            service_id: fromType === 'service' ? (slot.service_id || selectedServiceId) : undefined,
            start: timingObj.start || '',
            end: timingObj.end || '',
            custom_unavailabilities: slot.custom_unavailabilities || [],
        });
        setModalMode('edit');
        setModalError('');
        setShowModal(true);
    };
    const openAddUnavailModal = () => {
        setUnavailFormData({ visit_type: 'Tele Consult', from_date: dayjs(), to_date: dayjs().add(1, 'day'), from_time: dayjs().hour(9).minute(0), to_time: dayjs().hour(10).minute(0) });
        setUnavailMode('add');
        setUnavailModalError('');
        setShowUnavailModal(true);
    };
    const openEditUnavailModal = (unavail: any) => {
        setUnavailFormData({
            id: unavail.id,
            visit_type: unavail.visit_type,
            from_date: dayjs(unavail.from_date),
            to_date: dayjs(unavail.to_date),
            from_time: dayjs(unavail.timing.from_time, 'HH:mm'),
            to_time: dayjs(unavail.timing.to_time, 'HH:mm'),
        });
        setUnavailMode('edit');
        setUnavailModalError('');
        setShowUnavailModal(true);
    };
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!providerId) return;
        setModalError('');
        // Client-side validation for times (prevents server errors)
        if (!formData.start || !formData.end) {
            setModalError('Please select start and end times.');
            return;
        }
        const startTime = dayjs(
            fromType === 'service' ? formData.start : formData.start,
            fromType === 'service' ? 'hh:mm A' : 'HH:mm'
        );
        const endTime = dayjs(
            fromType === 'service' ? formData.end : formData.end,
            fromType === 'service' ? 'hh:mm A' : 'HH:mm'
        );
        if (startTime.isAfter(endTime) || startTime.isSame(endTime)) {
            setModalError('The end time must be after the start time.');
            return;
        }
        // Existing conflict checks...
        if (fromType === 'service' && modalMode === 'add' && !formData.visit_type) {
            setModalError('Please select a service.');
            return;
        }
        if (fromType === 'service' && modalMode === 'add' && !formData.service_id) {
            setModalError('Please select a service.');
            return;
        }
        let targetSlots: Slot[] = slots;
        let hasConflict = false;
        const conflictMessage = 'Availability for this day and visit type already exists.';
        if (modalMode === 'add' && fromType === 'service' && formData.service_id) {
            if (formData.service_id !== selectedServiceId) {
                try {
                    const res = await apiClient<any>(`/v1/resource/serviceAvailability?service_provider_id=${providerId}&service_id=${formData.service_id}`);
                    targetSlots = (res?.data?.service_availabilities || []).sort(
                        (a: Slot, b: Slot) => daysOfWeek.indexOf(a.day) - daysOfWeek.indexOf(b.day)
                    );
                } catch (fetchErr: any) {
                    console.error(fetchErr);
                    setModalError('Failed to check conflicts for selected service.');
                    return;
                }
            }
            const daysToCheck = formData.day === 'All' ? daysOfWeek : [formData.day];
            hasConflict = daysToCheck.some((day) =>
                targetSlots.some((slot) => slot.day === day && slot.visit_type === formData.visit_type)
            );
        } else if (modalMode === 'add') {
            const daysToCheck = formData.day === 'All' ? daysOfWeek : [formData.day];
            hasConflict = daysToCheck.some((day) =>
                slots.some((slot) => slot.day === day && slot.visit_type === formData.visit_type)
            );
        } else {
            hasConflict = slots.some((slot) =>
                slot.id !== formData.id &&
                slot.day === formData.day &&
                slot.visit_type === formData.visit_type
            );
        }
        if (hasConflict) {
            setModalError(conflictMessage);
            return;
        }
        try {
            if (fromType === 'service') {
                const serviceId = selectedServiceId || formData.service_id;
                const customUnavail = formData.custom_unavailabilities || [];
                if (modalMode === 'edit') {
                    const dayLower = formData.day.toLowerCase();
                    // Use object for edit (existingId case)
                    const timeRange = {
                        start: formData.start,
                        end: formData.end
                    };
                    const editPayload = {
                        service_id: serviceId,
                        service_provider_id: parseInt(providerId),
                        apply_to_all_days: false,
                        is_available: true,
                        created_by: 1,
                        day: dayLower,
                        time_range: timeRange,
                        ...(customUnavail.length > 0 ? { custom_unavailabilities: customUnavail } : {})
                    };
                    const editUrl = `v1/resource/serviceAvailability/${formData.id}`;
                    await apiClient<any>(editUrl, {
                        method: 'PUT',
                        body: editPayload
                    });
                } else {
                    // Use string for add (no existingId case)
                    const timeRangeString = `${formData.start}-${formData.end}`;
                    const daysToAdd = formData.day === 'All' ? daysOfWeek : [formData.day];
                    let addErrors: string[] = []; // Collect errors if multi-day
                    for (const day of daysToAdd) {
                        try {
                            const dayLower = day.toLowerCase();
                            const payload = {
                                service_id: serviceId,
                                service_provider_id: parseInt(providerId),
                                apply_to_all_days: false,
                                is_available: true,
                                created_by: 1,
                                day: dayLower,
                                time_range: timeRangeString, // Now a string for add
                                ...(customUnavail.length > 0 ? { custom_unavailabilities: customUnavail } : {})
                            };
                            await apiClient<any>('v1/resource/serviceAvailability', {
                                method: 'POST',
                                body: payload
                            });
                        } catch (dayErr: any) {
                            console.error(`Failed to add for ${day}:`, dayErr);
                            let dayErrorMsg = `Failed to add for ${day}`;
                            if (dayErr.response?.data) {
                                const backendData = dayErr.response.data;
                                console.log("backENDNJKFNDATAT",backendData)
                                if (backendData.data?.errors) {
                                    const validationErrors = Object.values(backendData.data.errors)
                                        .flat() // Handles string or ["msg"] arrays
                                        .filter(Boolean)
                                        .join('\n');
                                    dayErrorMsg += `: ${validationErrors || backendData.message || 'Unknown error'}`;
                                } else if (backendData.message) {
                                    dayErrorMsg += `: ${backendData.message}`;
                                } else {
                                    dayErrorMsg += ': Failed to create availability: Availability already exists for this service provider, service, and day.';
                                }
                            } else {
                                dayErrorMsg += ': Failed to create availability: Availability already exists for this service provider, service, and day';
                            }
                            addErrors.push(dayErrorMsg);
                            // Stop on first error (or continue and collect; adjust as needed)
                            break; // Remove this line if you want to try all days
                        }
                    }
                    if (addErrors.length > 0) {
                        throw new Error(addErrors.join('\n\n')); // Throw to outer catch for display
                    }
                }
            } else {
                // Existing vet logic (unchanged)...
                let availabilityDetails: any[];
                if (formData.day === 'All') {
                    availabilityDetails = daysOfWeek.map((day) => ({
                        day,
                        visit_type: formData.visit_type,
                        timing: { start: formData.start, end: formData.end }
                    }));
                } else {
                    availabilityDetails = [{
                        day: formData.day,
                        visit_type: formData.visit_type,
                        timing: { start: formData.start, end: formData.end }
                    }];
                }
                let payload: any;
                let postUrl = 'v1/resource/availabilityDetails';
                if (modalMode === 'add') {
                    payload = {
                        provider_id: parseInt(providerId),
                        availabilityDetails
                    };
                    await apiClient<any>(postUrl, {
                        method: 'POST',
                        body: payload
                    });
                } else {
                    let editPayload: any = {
                        provider_id: parseInt(providerId),
                        day: formData.day,
                        visit_type: formData.visit_type,
                        timing: { start: formData.start, end: formData.end }
                    };
                    let editUrl = `${postUrl}/${formData.id}`;
                    await apiClient<any>(editUrl, {
                        method: 'PUT',
                        body: editPayload
                    });
                }
            }
            setModalError('');
            setShowModal(false);
            if (fromType === 'service' && modalMode === 'add' && formData.service_id && formData.service_id !== selectedServiceId) {
                setSelectedServiceId(formData.service_id);
                fetchSlots(formData.service_id);
            } else if (fromType === 'service') {
                fetchSlots(selectedServiceId);
            } else {
                fetchSlots();
            }
            setError('');
        } catch (err: any) {
            console.error(err);
            let errorMessage = 'Failed to add availability.';
            if (modalMode === 'edit') {
                errorMessage = 'Failed to update availability.';
            }
            if (err.response?.data) {
                const backendData = err.response.data;
                if (backendData.data?.errors) {
                    const validationErrors = Object.values(backendData.data.errors)
                        .flat()
                        .filter(Boolean)
                        .join('\n');
                    errorMessage = validationErrors || backendData.message || errorMessage;
                } else if (backendData.message) {
                    errorMessage = backendData.message;
                }
            } else if (typeof err.message === 'string') {
                // Handle thrown multi-day errors
                errorMessage = err.message;
            }
            setModalError(errorMessage);
        }
    };
    const handleUnavailSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!providerId) return;
        setUnavailModalError('');
        if (!unavailFormData.visit_type) {
            setUnavailModalError('Please select a visit type.');
            return;
        }
        if (!unavailFormData.from_date || !unavailFormData.to_date || !unavailFormData.from_time || !unavailFormData.to_time) {
            setUnavailModalError('Please fill all fields.');
            return;
        }
        if (unavailFormData.from_date > unavailFormData.to_date) {
            setUnavailModalError('From date must be before or equal to to date.');
            return;
        }
        if (unavailFormData.from_time >= unavailFormData.to_time) {
            setUnavailModalError('From time must be before to time.');
            return;
        }
        try {
            const payload = {
                provider_id: parseInt(providerId),
                from_date: dayjs(unavailFormData.from_date).format('YYYY-MM-DD'),
                to_date: dayjs(unavailFormData.to_date).format('YYYY-MM-DD'),
                visit_type: unavailFormData.visit_type,
                timing: {
                    tz: 'Asia/Kolkata',
                    from_time: dayjs(unavailFormData.from_time).format('HH:mm'),
                    to_time: dayjs(unavailFormData.to_time).format('HH:mm'),
                },
            };
            let url = '/v1/resource/customAvailabilityDetails';
            let config: any = { method: unavailMode === 'add' ? 'POST' : 'PATCH', body: payload };
            if (unavailMode === 'edit') {
                url += `/${unavailFormData.id}`;
            }
            await apiClient<any>(url, config);
            setUnavailModalError('');
            setShowUnavailModal(false);
            await fetchCustomUnavailability();
        } catch (err: any) {
            console.error(err);
            let errorMessage = unavailMode === 'add' ? 'Failed to add unavailability.' : 'Failed to update unavailability.';
            if (err.response?.data) {
                const backendData = err.response.data;
                if (backendData.data?.errors) {
                    // Extract specific validation errors and join them with newline for multi-line display
                    const validationErrors = Object.values(backendData.data.errors).join('\n');
                    errorMessage = validationErrors || backendData.message || errorMessage;
                } else if (backendData.message) {
                    errorMessage = backendData.message;
                }
            }
            setUnavailModalError(errorMessage);
        }
    };
    const handleFormChange = (field: keyof FormData, value: any) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };
    const handleUnavailFormChange = (field: keyof UnavailFormData, value: Dayjs | null | string) => {
        setUnavailFormData(prev => ({ ...prev, [field]: value }));
    };
    const deleteSlot = async (id: number) => {
        if (!window.confirm('Are you sure you want to delete this availability slot?')) return;
        try {
            let deleteUrl = `v1/resource/${fromType === 'service' ? 'serviceAvailability' : 'availabilityDetails'}/${id}`;
            await apiClient<any>(deleteUrl, {
                method: 'DELETE',
            });
            if (fromType === 'service') {
                fetchSlots(selectedServiceId);
            } else {
                fetchSlots();
            }
            setError('');
        } catch (err: any) {
            console.error(err);
            setError('Failed to delete availability slot.');
        }
    };
    const deleteUnavail = async (id: number) => {
        if (!window.confirm('Are you sure you want to delete this unavailability?')) return;
        try {
            const deleteUrl = `/v1/resource/customAvailabilityDetails/${id}`;
            await apiClient<any>(deleteUrl, {
                method: 'DELETE',
            });
            await fetchCustomUnavailability();
        } catch (err: any) {
            console.error(err);
            setError('Failed to delete unavailability.');
        }
    };
    const handleGoBack = () => {
        navigate(-1);
    };
    if (!providerId) {
        return (
            <Layout>
                <div
                    style={{
                        minHeight: "60vh",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexDirection: "column",
                        color: "#ef4444",
                        fontSize: "18px",
                    }}
                >
                    <div style={{ fontSize: "48px", marginBottom: "16px" }}>⚠️</div>
                    <div>Provider information is missing</div>
                    <button
                        onClick={handleGoBack}
                        style={{
                            marginTop: "20px",
                            padding: "12px 24px",
                            backgroundColor: "#3b82f6",
                            color: "white",
                            border: "none",
                            borderRadius: "8px",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                        }}
                    >
                        <ArrowLeft size={18} />
                        Go Back
                    </button>
                </div>
            </Layout>
        );
    }
    const showAddButton = !loading && !error && (fromType === "vet" || (fromType === "service" && services.length > 0));
    const addButtons = showAddButton && (
        <div style={{ display: 'flex', gap: '12px', marginBottom: '24px' }}>
            <button
                onClick={openAddModal}
                style={{
                    padding: "12px 24px",
                    backgroundColor: "#10b981",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px",
                    fontSize: "1rem",
                    fontWeight: 500,
                }}
            >
                <Calendar size={18} />
                Add Availability
            </button>
            {fromType === 'vet' && (
                <button
                    onClick={openAddUnavailModal}
                    style={{
                        padding: "12px 24px",
                        backgroundColor: "#ef4444",
                        color: "white",
                        border: "none",
                        borderRadius: "8px",
                        cursor: "pointer",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "8px",
                        fontSize: "1rem",
                        fontWeight: 500,
                    }}
                >
                    <Calendar size={18} />
                    Add Unavailability
                </button>
            )}
        </div>
    );
    return (
        <Layout>
            <div>
                <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "0 20px" }}>
                    <div style={{ padding: "20px 0", display: "flex", alignItems: "center" }}>
                        <button
                            onClick={handleGoBack}
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                padding: "10px 16px",
                                backgroundColor: "white",
                                color: "#374151",
                                border: "1px solid #d1d5db",
                                borderRadius: "12px",
                                fontSize: "0.95rem",
                                fontWeight: 500,
                                cursor: "pointer",
                                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                                transition: "all 0.2s ease",
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = "#f9fafb";
                                e.currentTarget.style.borderColor = "#9ca3af";
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = "white";
                                e.currentTarget.style.borderColor = "#d1d5db";
                            }}
                        >
                            <ArrowLeft size={18} />
                            Back
                        </button>
                    </div>
                    <div
                        style={{
                            padding: "20px 20px 40px",
                            textAlign: "center",
                            color: "#1f2937",
                        }}
                    >
                        <h1
                            style={{
                                fontSize: "2.5rem",
                                fontWeight: "700",
                                margin: "0 0 8px 0",
                                textShadow: "0 2px 4px rgba(0,0,0,0.3)",
                            }}
                        >
                            {fromType === 'service' ? 'Availability of the service provider' : 'Availability of the Veterinarian'}
                        </h1>
                        <p style={{ fontSize: "1.1rem", opacity: 0.9, margin: 0, fontWeight: 300 }}>
                            Choose your preferred time slot with Provider #{providerId}
                        </p>
                        {fromType === "service" && services.length > 0 && (
                            <div style={{ marginTop: "20px" }}>
                                <select
                                    value={selectedServiceId?.toString() ?? ""}
                                    onChange={(e) => {
                                        const idStr = e.target.value;
                                        if (idStr === "") {
                                            setSelectedServiceId(undefined);
                                            return;
                                        }
                                        const id = parseInt(idStr);
                                        if (!isNaN(id)) {
                                            setSelectedServiceId(id);
                                            fetchSlots(id);
                                        }
                                    }}
                                    style={{
                                        padding: "10px 16px",
                                        borderRadius: "12px",
                                        border: "1px solid #d1d5db",
                                        backgroundColor: "white",
                                        fontSize: "1rem",
                                    }}
                                >
                                    <option value="">Select a service</option>
                                    {services.map((s) => (
                                        <option key={s.id} value={s.id}>
                                            {s.services_name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}
                    </div>
                    <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "0 20px" }}>
                        <div
                            style={{
                                background: "rgba(255, 255, 255, 0.95)",
                                borderRadius: "20px",
                                padding: "32px",
                                boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
                            }}
                        >
                            {!loading && slots.length > 0 && (
                                <div
                                    style={{
                                        padding: "8px 16px",
                                        // backgroundColor: "#10b981",
                                        color: "#10b981",
                                        borderRadius: "20px",
                                        fontSize: "0.875rem",
                                        fontWeight: 500,
                                        marginBottom: "24px",
                                    }}
                                >
                                    {slots.length} slot{slots.length !== 1 ? "s" : ""} available
                                </div>
                            )}
                            {addButtons}
                            {loading ? (
                                <div
                                    style={{
                                        textAlign: "center",
                                        padding: "60px 20px",
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        gap: "16px",
                                    }}
                                >
                                    <div className="modern-loader" />
                                    <p style={{ color: "#6b7280", margin: 0 }}>Loading available slots...</p>
                                </div>
                            ) : error ? (
                                <div
                                    style={{
                                        textAlign: "center",
                                        padding: "60px 20px",
                                        color: "#ef4444",
                                    }}
                                >
                                    <div style={{ fontSize: "48px", marginBottom: "16px" }}>😞</div>
                                    <p style={{ fontSize: "1.125rem", margin: "0 0 16px 0" }}>{error}</p>
                                </div>
                            ) : slots.length === 0 ? (
                                <div
                                    style={{
                                        textAlign: "center",
                                        padding: "60px 20px",
                                        color: "#6b7280",
                                    }}
                                >
                                    <div style={{ fontSize: "48px", marginBottom: "16px" }}>📅</div>
                                    <p style={{ fontSize: "1.125rem", margin: 0 }}>
                                        No availability slots configured
                                    </p>
                                    <p style={{ fontSize: "0.875rem", margin: "8px 0 0 0" }}>
                                        {fromType === "service" ? "Select a service from the dropdown above and add availability slots." : "Try adding availability slots"}
                                    </p>
                                </div>
                            ) : (
                                <>
                                    <div
                                        style={{
                                            display: "grid",
                                            gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
                                            gap: "20px",
                                        }}
                                    >
                                        {slots.map((slot) => (
                                            <SlotCard
                                                key={slot.id}
                                                slot={slot}
                                                fromType={fromType}
                                                services={services}
                                                hoveredSlot={hoveredSlot}
                                                onMouseEnter={setHoveredSlot}
                                                onMouseLeave={() => setHoveredSlot(null)}
                                                onEdit={openEditModal}
                                                onDelete={deleteSlot}
                                            />
                                        ))}
                                    </div>
                                    {fromType === "vet" && (
                                        <UnavailabilitySection
                                            customSlots={customSlotsSorted}
                                            onEdit={openEditUnavailModal}
                                            onDelete={deleteUnavail}
                                        />
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <AvailabilityModal
                isOpen={showModal}
                mode={modalMode}
                formData={formData}
                onClose={() => { setShowModal(false); setModalError(''); }}
                onSubmit={handleSubmit}
                onFieldChange={handleFormChange}
                modalError={modalError}
                daysOfWeek={daysOfWeek}
                visitTypes={visitTypes}
                fromType={fromType}
                services={services}
            />
            <UnavailabilityModal
                isOpen={showUnavailModal}
                mode={unavailMode}
                formData={unavailFormData}
                onClose={() => { setShowUnavailModal(false); setUnavailModalError(''); }}
                onSubmit={handleUnavailSubmit}
                onFieldChange={handleUnavailFormChange}
                modalError={unavailModalError}
                visitTypes={visitTypes}
            />
            <style>
                {`
          .modern-loader {
            width: 50px;
            height: 50px;
            border: 4px solid #f3f4f6;
            border-top: 4px solid #3b82f6;
            border-radius: 50%;
            animation: modern-spin 1s cubic-bezier(0.68, -0.55, 0.265, 1.55) infinite;
          }
          @keyframes modern-spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          html { scroll-behavior: smooth; }
          ::-webkit-scrollbar { width: 8px; }
          ::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 10px; }
          ::-webkit-scrollbar-thumb { background: linear-gradient(135deg, #667eea, #764ba2); border-radius: 10px; }
          ::-webkit-scrollbar-thumb:hover { background: linear-gradient(135deg, #5a67d8, #6b46c1); }
        `}
            </style>
        </Layout>
    );
};
export default AvailabilityScreen;